import { useCallback } from 'react'
import { ProductOpeningHoursResponse } from '@leid/api-locations'
import { ProductAvailabilityInfo } from '@leid/api-orders'
import { addDays, addHours, endOfDay, endOfYesterday, isBefore, startOfDay, startOfHour, subDays } from 'date-fns'

import { buildDate } from 'app/utils/dates'
import { CartFormValues, OrderInfo } from 'appTypes/orders'
import { DateTimePickerDatePredicate } from 'components/form/DateTimePicker/interfaces'
import {
  checkIfPeriodStartsEndsInWorkingHours,
  getClosestUnavailableDate,
  getLocationAvailabilityInfo,
} from 'helpers/utils/availabilities'

const getNearestFullyDisabledDay = (date: Date) => (date.getHours() === 0 ? date : startOfDay(addDays(date, 1)))

export const useShouldDisableReturnDate = (
  values: CartFormValues,
  initialValues: CartFormValues,
  isProlonging: boolean,
  order: OrderInfo | undefined,
  productLocationAvailability: ProductAvailabilityInfo,
  openingHoursInfo: ProductOpeningHoursResponse[],
): DateTimePickerDatePredicate => {
  return useCallback(
    (date) => {
      if (isBefore(date, endOfYesterday())) {
        return true
      }

      const locationAvailability = values.selectedLocationId
        ? getLocationAvailabilityInfo(values.selectedLocationId, productLocationAvailability)
        : undefined

      const isRerent = order?.order.isRerent ?? false

      if (isProlonging) {
        if (initialValues.endDate) {
          if (isBefore(initialValues.endDate, endOfYesterday())) {
            // if end date is already in the past
            let now = new Date()
            now = startOfHour(addHours(now, 1))

            const nearestDisabledDate = getClosestUnavailableDate(now, locationAvailability, isRerent, values.amount)
            if (nearestDisabledDate) {
              const nearestFullyDisabledDay = getNearestFullyDisabledDay(nearestDisabledDate)
              if (!isBefore(date, nearestFullyDisabledDay)) {
                return true
              }
            }
          } else {
            // if end date is already today or in the future
            const endOfPreviousDay = endOfDay(subDays(initialValues.endDate, 1))
            if (isBefore(date, endOfPreviousDay)) {
              return true
            }

            const rentedTo = startOfHour(buildDate(initialValues.endDate, initialValues.returnAt))
            const now = startOfHour(addHours(new Date(), 1))

            const rentedToDate = isBefore(rentedTo, now) ? now : rentedTo

            const nearestDisabledDate = getClosestUnavailableDate(
              rentedToDate,
              locationAvailability,
              isRerent,
              values.amount,
            )
            if (nearestDisabledDate) {
              const shiftedNearestDisabledDate = addHours(nearestDisabledDate, 1)
              const nearestFullyDisabledDay = getNearestFullyDisabledDay(shiftedNearestDisabledDate)

              if (!isBefore(date, nearestFullyDisabledDay)) {
                return true
              }
            }
          }
        }
      } else {
        if (values.startDate) {
          const endOfPreviousDay = endOfDay(subDays(values.startDate, 1))
          if (isBefore(date, endOfPreviousDay)) {
            return true
          }

          const fullStartDate = startOfHour(buildDate(values.startDate, values.pickupAt))

          const nearestDisabledDate = getClosestUnavailableDate(
            fullStartDate,
            locationAvailability,
            isRerent,
            values.amount,
          )
          if (nearestDisabledDate) {
            const shiftedNearestDisabledDate = addHours(nearestDisabledDate, 1)
            const nearestFullyDisabledDay = getNearestFullyDisabledDay(shiftedNearestDisabledDate)
            if (!isBefore(date, nearestFullyDisabledDay)) {
              return true
            }
          }
        }
      }

      if (values.selectedLocationId) {
        const start = startOfDay(date)
        const end = endOfDay(start)

        // Check opening hours for start and end of period
        const startsOrEndsInWorkingHours = checkIfPeriodStartsEndsInWorkingHours(
          start,
          end,
          values.selectedLocationId,
          false, // Check only that it's end time
          openingHoursInfo,
          false, // Do not check time, only check that location is not closed
        )
        if (!startsOrEndsInWorkingHours) {
          // It's not in working hours or day
          return true
        }
      }

      return false
    },
    [
      isProlonging,
      values.startDate,
      values.pickupAt,
      values.selectedLocationId,
      values.amount,
      openingHoursInfo,
      productLocationAvailability,
      initialValues.endDate,
      initialValues.returnAt,
      order?.order.isRerent,
    ],
  )
}
